import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

// External Libraries
import { GIButton, Modal } from 'app-components';

// Components
import APTCSlider from '../APTCSlider';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';

// Constants
import { ALERT_TYPES } from '../../../../constants/individualPortal/dashboard/dashboardModelTypes';

// Hooks
import { useAppDispatch } from '../../../../hooks';
import usePlanSummary from '../hooks/usePlanSummary';
import { useAptcContext } from '../hooks/useAptcContext';
import { useAppSelector } from '@hooks/index';
import { environmentSelector } from '@selectors/commonSelectors';

// Contexts
import { ModalContextType } from '../../../../contexts/modalContext';

// Utilities
import { formatCurrency } from '../../../../utils/tools/formatCurrenyToInteger';

// Styles
import {
    StyledAPTCContainer,
    StyledAptcModalBodyContainer,
    StyledAptcSectionHeader,
    StyledAPTCSection2Container,
    StyledAptcTdHeader,
    StyledAptcTrHeader,
    StyledBorderlessTable,
    StyledBorderlessTd,
    StyledBorderlessTr,
    StyledModalButtonFooter,
    StyledVerticalAlignTd,
    StyledAlertMessage,
} from '../plansummary.styled';

// Modals
import { PlanSummaryModals } from './planSummaryModals';
import { IPlan, ISaveAptcRequest } from '../planSummary.types';
import { getPlanSummaryData } from '../../../../actions/individualPortal/planSummary/planSummaryActionCreators';

// Constants
import { PLAN_TYPE } from '../../../../modules/custom-grouping/modals/modalTypes';
import { EnrollmentIdType } from '../planSummary.constants';
import { isWithinRange } from '../planSummary.utils';
import { IEX_STATE_CODES } from '@constants/configs/states';

const MIN_APTC_VALUE = 0;

const APTCSection1 = ({ maxAptc }) => {
    const { isAptcSectionTableView, aptcHeadingFontSize } = usePlanSummary();

    return (
        <>
            <StyledAptcSectionHeader fontSize={aptcHeadingFontSize}>
                <FormattedMessage
                    id="plansummary.atpcModal.aptcExplanation"
                    values={{ maxAptc: formatCurrency(maxAptc) }}
                />
            </StyledAptcSectionHeader>
            <ConditionalRendering isTrue={!isAptcSectionTableView}>
                <hr />
                <p>
                    <FormattedMessage id="plansummary.atpcModal.usageAptc" />
                </p>
                <StyledAlertMessage
                    content={
                        <>
                            <p>
                                <strong>
                                    <FormattedMessage id="plansummary.atpcModal.aptcCredit" />
                                </strong>
                            </p>
                            <p>
                                <FormattedMessage id="plansummary.atpcModal.aptcCreditContent" />
                            </p>
                        </>
                    }
                    type={ALERT_TYPES.INFO}
                    showIcon={false}
                />

                <StyledAlertMessage
                    content={
                        <>
                            <p>
                                <strong>
                                    <FormattedMessage id="plansummary.atpcModal.aptcmonthlyAdv" />
                                </strong>
                            </p>
                            <p>
                                <FormattedMessage id="plansummary.atpcModal.aptcmonthlyAdvContent" />
                            </p>
                        </>
                    }
                    type={ALERT_TYPES.INFO}
                    showIcon={false}
                />

                <p>
                    <FormattedMessage id="plansummary.atpcModal.instruction1" />
                </p>
            </ConditionalRendering>

            <ConditionalRendering isTrue={isAptcSectionTableView}>
                <div>
                    <div>
                        <p>
                            <FormattedMessage id="plansummary.atpcModal.usageAptc" />
                        </p>
                    </div>
                    <StyledBorderlessTable>
                        <tbody>
                            <StyledAptcTrHeader>
                                <StyledAptcTdHeader className="aptc-td-header">
                                    <FormattedMessage id="plansummary.atpcModal.aptcmonthlyAdv" />
                                </StyledAptcTdHeader>
                                <StyledAptcTdHeader className="aptc-td-header">
                                    <FormattedMessage id="plansummary.atpcModal.aptcCredit" />
                                </StyledAptcTdHeader>
                            </StyledAptcTrHeader>
                            <StyledBorderlessTr>
                                <StyledBorderlessTd>
                                    <FormattedMessage id="plansummary.atpcModal.aptcmonthlyAdvContent" />
                                </StyledBorderlessTd>
                                <StyledVerticalAlignTd>
                                    <FormattedMessage id="plansummary.atpcModal.aptcCreditContent" />
                                </StyledVerticalAlignTd>
                            </StyledBorderlessTr>
                            <StyledBorderlessTr>
                                <StyledVerticalAlignTd>
                                    <b>
                                        <FormattedMessage id="plansummary.atpcModal.aptcmonthlyAdvContent.pro" />
                                    </b>
                                    <FormattedMessage id="plansummary.atpcModal.aptcmonthlyAdvContent.pro.content" />
                                </StyledVerticalAlignTd>
                                <StyledBorderlessTd>
                                    <b>
                                        <FormattedMessage id="plansummary.atpcModal.aptcCreditContent.pro" />
                                    </b>
                                    <FormattedMessage id="plansummary.atpcModal.aptcCreditContent.pro.content" />
                                </StyledBorderlessTd>
                            </StyledBorderlessTr>
                            <StyledBorderlessTr>
                                <StyledBorderlessTd>
                                    <b>
                                        <FormattedMessage id="plansummary.atpcModal.aptcmonthlyAdvContent.con" />
                                    </b>
                                    <FormattedMessage id="plansummary.atpcModal.aptcmonthlyAdvContent.con.content" />
                                </StyledBorderlessTd>
                                <StyledVerticalAlignTd>
                                    <b>
                                        <FormattedMessage id="plansummary.atpcModal.aptcCreditContent.con" />
                                    </b>
                                    <FormattedMessage id="plansummary.atpcModal.aptcCreditContent.con.content" />
                                </StyledVerticalAlignTd>
                            </StyledBorderlessTr>
                        </tbody>
                    </StyledBorderlessTable>
                </div>
            </ConditionalRendering>
        </>
    );
};

const APTCSection2 = ({ maxAptc, premiumEffDate, coverageStartDate }) => {
    const coverageStartYear = coverageStartDate.split('/')[2];
    const [showPremiumEffDate, setShowPremiumEffDate] = useState(false);
    const { aptcHeadingFontSize } = usePlanSummary();
    const { newAptc, electedAptc } = useAptcContext();
    const {stateCode} = useAppSelector(environmentSelector);
    const APTC_DOLLAR_STATES = [IEX_STATE_CODES.MN];
    const isAptcDollarVisible = APTC_DOLLAR_STATES.includes(stateCode);

    useEffect(() => {
        if (!showPremiumEffDate && newAptc !== electedAptc) {
            setShowPremiumEffDate(true);
        }
    }, [newAptc]);

    return (
        <StyledAPTCSection2Container>
            <StyledAptcSectionHeader fontSize={aptcHeadingFontSize}>
                <FormattedMessage id="plansummary.atpcModal.sliderInstruction" />
            </StyledAptcSectionHeader>
            <div>
                <StyledAPTCContainer isDollarVisible = {isAptcDollarVisible}>
                    <span>
                        <label htmlFor="montax">
                            <FormattedMessage id="plansummary.atpcModal.sliderCredit" />
                        </label>
                    </span>
                    <APTCSlider maxAptc={maxAptc} />
                </StyledAPTCContainer>
                <ConditionalRendering isTrue={showPremiumEffDate}>
                    <StyledAlertMessage
                        content={
                            <p>
                                <FormattedMessage
                                    id="plansummary.atpcModal.effectiveDate"
                                    values={{ premiumEffDate }}
                                />
                            </p>
                        }
                        type={ALERT_TYPES.INFO}
                        showIcon={false}
                    />
                </ConditionalRendering>
                <div>
                    <StyledAptcSectionHeader fontSize={aptcHeadingFontSize}>
                        <FormattedMessage id="plansummary.atpcModal.amountTitle" />
                    </StyledAptcSectionHeader>
                    <ul>
                        <li>
                            <FormattedMessage id="plansummary.atpcModal.amountContent1" />
                        </li>
                        <li>
                            <FormattedMessage
                                id="plansummary.atpcModal.amountContent2"
                                values={{ coverageStartYear }}
                            />
                        </li>
                        <li>
                            <FormattedMessage
                                id="plansummary.atpcModal.amountContent3"
                                values={{ coverageStartYear }}
                            />
                        </li>
                    </ul>
                </div>
            </div>
        </StyledAPTCSection2Container>
    );
};

const APTCSuccessModal = ({ maxAptc, premiumEffDate, plan, onClose, openModal }) => {
    const [activeScreen, setActiveScreen] = useState(1);
    const { newAptc, electedAptc } = useAptcContext();
    const dispatch = useAppDispatch();
    const { saveAptc, aptcModalHeight, fetchPlanSummaryData } = usePlanSummary();
    const { caseNumber, typeOfPlan, coverageStartDate } = plan;

    const onAptcConfirm = async () => {
        const payload: ISaveAptcRequest = {
            caseNumber,
            [typeOfPlan.toUpperCase() === PLAN_TYPE.HEALTH ? EnrollmentIdType.HEALTH : EnrollmentIdType.DENTAL]:
                plan.enrollmentId,
            aptcAmt: newAptc,
            aptcPremiumEffectiveDate: premiumEffDate,
        };
        const success = await saveAptc(payload);
        if (success) {
            fetchPlanSummaryData();
            onClose();
        } else {
            openModal(PlanSummaryModals.APTCFailure);
        }
    };

    const getSubmitBtn = () => {
        return activeScreen === 1
            ? {
                id: 'adjust_tax_credit_btn',
                labelId: 'plansummary.atpcModal.adjustAptc',
                onClickHandler: () => setActiveScreen(2),
            }
            : {
                id: 'confirm_aptc_btn',
                labelId: 'button.confirm',
                onClickHandler: onAptcConfirm,
                isDisabled: newAptc === electedAptc || !isWithinRange(newAptc, MIN_APTC_VALUE, maxAptc),
            };
    };

    return (
        <Modal data-testid="aptc-success-modal" id="aptc_modal">
            <Modal.Header id="modal_header" titleId={'plansummary.atpcModal.header'} onHide={onClose} />
            <Modal.Body>
                <StyledAptcModalBodyContainer height={aptcModalHeight}>
                    <ConditionalRendering isTrue={activeScreen === 1}>
                        <APTCSection1 maxAptc={maxAptc} />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={activeScreen === 2}>
                        <APTCSection2
                            maxAptc={maxAptc}
                            premiumEffDate={premiumEffDate}
                            coverageStartDate={coverageStartDate}
                        />
                    </ConditionalRendering>
                </StyledAptcModalBodyContainer>
            </Modal.Body>
            <Modal.Controls>
                <StyledModalButtonFooter>
                    <ConditionalRendering isTrue={activeScreen === 2}>
                        <GIButton
                            id="back_btn"
                            labelId="button.back"
                            variant="secondary"
                            onClickHandler={() => setActiveScreen(1)}
                        />
                        <div />
                    </ConditionalRendering>
                    <GIButton id="close_btn" labelId="button.close" variant="secondary" onClickHandler={onClose} />
                    <GIButton {...getSubmitBtn()} />
                </StyledModalButtonFooter>
            </Modal.Controls>
        </Modal>
    );
};

export default APTCSuccessModal;
